import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    TextField,
    Tooltip,
    Typography,
    Chip,
    IconButton
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import dayjs from "dayjs";
import { AppointmentCard, CommonPatientDetails } from "../../../Components/Common Components/CommonComponents";
import Loader from "../../../Components/Loader";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import { Colors } from "../../../Styles/Colors";
import "../../FrontOffice/OutPatients/OutpatientHome.css";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { renderIconButton } from "../../Doctor/DoctorNotes/Common/DocCommonComponent";
import { ImagePaths } from "../../../Utility/ImagePaths";
import { setCachevalue } from "../../../Utility/Services/CacheProviderService";

const statusColorMap = {
    Pending: { bg: "#fff2daff", color: "#B67200" },
    Scheduled: { bg: "#ddf3feff", color: "#034AA6" },
    "Medication Ready": { bg: "#ffeaf7ff", color: "#B21792" },
    "Medication InProgress": { bg: "#e0ffdeff", color: "#1A9B1F" },
    Completed: { bg: "#e7ffeaff", color: "#047820" }
};

const WalkInAppointments = () => {
    const [queueData, setQueueData] = useState([]);
    const [filterDate, setFilterDate] = useState(new Date());
    const [isLoading, setIsLoading] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(""); // "" => All
    const [errorMsg, seterrorMsg] = useState(false);
    const [errorMsgText, seterrorMsgText] = useState('');
    const [successMsg, setsuccessMsg] = useState(false);
    const [successMsgText, setsuccessMsgText] = useState('');
    const [searchKey, setSearchKey] = useState("");
    const [statusCount, setStatusCount] = useState({});
    const pageSizeOptions = [10, 20, 30];

    const errorMessage = (msg) => {
        seterrorMsgText(msg)
        seterrorMsg(true)
        setIsLoading(false)
    }

    const successMessage = (msg) => {
        setsuccessMsgText(msg)
        setsuccessMsg(true)
        setIsLoading(false)
    }

    const msgClose = () => {
        seterrorMsg(false);
        seterrorMsgText('');
        setsuccessMsg(false);
        setsuccessMsgText('');
    }

    useEffect(() => {
        fetchAppointments();
    }, [filterDate, selectedStatus]);

    // Helper function to format therapists list
    const formatTherapists = (therapists) => {
        if (!therapists || !Array.isArray(therapists) || therapists.length === 0) {
            return { display: "-", full: "-" };
        }
        const therapistNames = therapists.map(t => t.therapist_name || t.name || "").filter(Boolean);
        if (therapistNames.length === 0) return { display: "-", full: "-" };
        const fullList = therapistNames.join(", ");
        return { display: fullList, full: fullList };
    };

    // Helper function to format treatments list
    const formatTreatments = (treatments) => {
        if (!treatments || !Array.isArray(treatments) || treatments.length === 0) {
            return { display: "-", full: "-" };
        }
        const treatmentNames = treatments.map(t => t.treatment_name || t.name || "").filter(Boolean);
        if (treatmentNames.length === 0) return { display: "-", full: "-" };
        const fullList = treatmentNames.join(", ");
        return { display: fullList, full: fullList };
    };

    // Helper function to format duration from treatments
    const formatDuration = (treatments) => {
        if (!treatments || !Array.isArray(treatments) || treatments.length === 0) {
            return "-";
        }
        // Get first treatment duration or combine if multiple
        const durations = treatments
            .map(t => {
                const duration = t.duration || 0;
                const mode = t.duration_mode || "Mins";
                return `${duration} ${mode}`;
            })
            .filter(Boolean);
        return durations.length > 0 ? durations.join(", ") : "-";
    };

    const fetchAppointments = useCallback(() => {
        setIsLoading(true);
        try {
            // Format date as DD-MM-YYYY to match API response format
            const appointmentDate = filterDate
                ? dayjs(filterDate).format("YYYY-MM-DD")
                : "";
            const status = selectedStatus || "";
            
            // Build query string with appointment_date and status parameters
            const qs = `?appointment_date=${appointmentDate}&status=${status}`;
            
            RestAPIService.getAll(Serviceurls.AYURVEDA_WALKIN_APPOINTMENT_LIST + qs)
                .then((res) => {
                    if (res.data.status === "success") {
                        // Map API response to match existing data structure
                        const list = (res.data.data || []).map((row, idx) => {
                            const therapists = formatTherapists(row.therapists);
                            const treatments = formatTreatments(row.treatments);
                            
                            return {
                                ...row,
                                sno: idx + 1,
                                // Map all API fields
                                appointment_date: row.appointment_date || "",
                                start_time: row.start_time || "",
                                end_time: row.end_time || "",
                                status: row.status || "",
                                room: row.room_name || "",
                                therapy_room_id: row.therapy_room_id || null,
                                session_id: row.session_id || null,
                                walkin_id: row.walkin_id || null,
                                patient_id: row.patient_id || null,
                                doctor_id: row.doctor_id || null,
                                // Patient details
                                name: row.patient_name?.trim() || "",
                                patient_name: row.patient_name?.trim() || "",
                                patient_age: row.patient_age || "",
                                age: row.patient_age || "",
                                mobile_no: row.patient_mobile_number || "",
                                patient_mobile_number: row.patient_mobile_number || "",
                                uh_id: row.patient_account_number || "",
                                patient_account_number: row.patient_account_number || "",
                                patient_gender: row.patient_gender || "",
                                photo_url: row.photo_url || null,
                                // Doctor details
                                doctor_name: row.doctor_name || "-",
                                // Therapists - store both display and full for tooltip
                                therapist: therapists.display,
                                therapistFull: therapists.full,
                                therapists: row.therapists || [],
                                // Treatments - store both display and full for tooltip
                                treatment: treatments.display,
                                treatmentFull: treatments.full,
                                treatments: row.treatments || [],
                                // Duration
                                duration: formatDuration(row.treatments),
                                // Other fields
                                cancelled_reason: row.cancelled_reason || null,
                                arrived_time: row.arrived_time || null,
                                completed_time: row.completed_time || null,
                                cancelled_by: row.cancelled_by || null,
                                medication_start_time: row.medication_start_time || null,
                                medication_end_time: row.medication_end_time || null,
                                started_time: row.started_time || null,
                                cancelled_date: row.cancelled_date || null,
                                stopped_time: row.stopped_time || [],
                            };
                        });
                        setQueueData(list);
                        // Set status count if provided in response
                        setStatusCount(res.data || {});
                    } else {
                        setQueueData([]);
                        errorMessage(res.data.message || "Failed to fetch appointments");
                    }
                })
                .catch((e) => errorMessage(e?.response?.data?.message || e.message))
                .finally(() => setIsLoading(false));
        } catch (e) {
            setIsLoading(false);
            errorMessage(e.message);
        }
    }, [filterDate, selectedStatus, searchKey]);

    const arriveChange = (row, status) => {
        setIsLoading(true);
        try {
            const payload = {
                id: row.id,
                status: status
            };
            RestAPIService.create(payload, Serviceurls.AYURVEDA_WALKIN_APPOINTMENT_STATUS_UPDATE)
                .then((res) => {
                    if (res.data.status === "success") {
                        successMessage(res.data.status)
                    } else {
                        errorMessage(res.data.message || "Failed to Change Status");
                    }
                })
                .catch((e) => errorMessage(e?.response?.data?.message || e.message))
                .finally(() => setIsLoading(false));
        } catch (e) {
            setIsLoading(false);
            errorMessage(e.message);
        }
    }

    const confirmReschedule = () => {
        if (!selectedToReschedule || !rescheduleDate) {
            errorMessage("Select a date");
            return;
        }
        // prevent selecting past date
        const min = dayjs().startOf("day");
        if (dayjs(rescheduleDate).isBefore(min, "day")) {
            errorMessage("Date cannot be in the past");
            return;
        }
        const payload = {
            status: "Rescheduled",
            appointment_id: selectedToReschedule?.id,
            start_date: dayjs(rescheduleDate).format("YYYY-MM-DD"),
        };
        updateAppointment(payload);
        setRescheduleDialogOpen(false);
    };

    const onCardClick = (statusKey) => {
        setSelectedStatus(statusKey);
    };

    const renderStatusChip = (status) => {
        const clr = statusColorMap[status] || { bg: "#E0E0E0", color: "#424242" };
        return (
            <Chip
                label={status}
                size="medium"
                sx={{
                    backgroundColor: clr.bg,
                    border: `1px solid ${clr.color}`,
                    px: 1,
                    py: 0.4,
                    borderRadius: "3px",
                    color: clr.color,
                    width: "90%",
                    height: "5dvh",
                    textAlign: "center",
                }}
            />
        );
    };


    const columns = [
        {
            field: "patientDetails",
            headerName: "Patient Details",
            flex: 0.22,
            renderCell: ({ row }) => <CommonPatientDetails data={row} />
        },
        {
            field: "doctor",
            headerName: "Doctor Name",
            flex: 0.13,
            renderCell: ({ row }) => (
                <Typography fontSize={"0.85vw"}>{row?.doctor_name || "-"}</Typography>
            )
        },
        {
            field: "treatment",
            headerName: "Treatment Details",
            flex: 0.13,
            renderCell: ({ row }) => {
                const displayText = row.treatment || "-";
                const fullText = row.treatmentFull || displayText;
                const hasMultiple = row.treatments && row.treatments.length > 1;
                const showTooltip = hasMultiple || displayText.length > 20;
                
                return (
                    <Tooltip title={showTooltip ? fullText : ""} arrow>
                        <Typography 
                            fontSize={"0.85vw"}
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "100%"
                            }}
                        >
                            {displayText}
                        </Typography>
                    </Tooltip>
                );
            }
        },
        {
            field: "duration",
            headerName: "Duration",
            flex: 0.10,
            renderCell: ({ row }) => <Typography fontSize={"0.85vw"}>{row.duration}</Typography>
        },
        {
            field: "therapist",
            headerName: "Therapist",
            flex: 0.12,
            renderCell: ({ row }) => {
                const displayText = row.therapist || "-";
                const fullText = row.therapistFull || displayText;
                const hasMultiple = row.therapists && row.therapists.length > 1;
                const showTooltip = hasMultiple || displayText.length > 20;
                
                return (
                    <Tooltip title={showTooltip ? fullText : ""} arrow>
                        <Typography 
                            fontSize={"0.85vw"}
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "100%"
                            }}
                        >
                            {displayText}
                        </Typography>
                    </Tooltip>
                );
            }
        },
        {
            field: "room",
            headerName: "Therapist Room",
            flex: 0.13,
            renderCell: ({ row }) => <Typography fontSize={"0.85vw"}>{row.room}</Typography>
        },
        {
            field: "start_time",
            headerName: "Start Time",
            flex: 0.09
        },
        {
            field: "end_time",
            headerName: "End Time",
            flex: 0.09
        },
        {
            field: "status",
            headerName: "Status",
            flex: 0.15,
            renderCell: ({ row }) => renderStatusChip(row.status)
        },
        {
            field: "actions",
            headerName: "Action",
            flex: 0.08,
            align: "center",
            renderCell: ({ row }) => (
                renderIconButton(
                    "Arrived",
                    ImagePaths.Arrived.default,
                    () => {
                        arriveChange(row, "Arrived");
                    },
                    false,
                    "1.6vw"
                )
            )
        }
    ];

    return (
        <Box sx={{ height: "85vh", backgroundColor: Colors.Background, p: 1 }}>
            <div className='' style={{ justifyContent: "end" }}>
                {/* <Typography class="">Counsellor</Typography> */}
                <div className='eMed_Header_OP_home_div' style={{ justifyContent: "end" }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={filterDate ? filterDate : null}
                            name={"datePicker"}
                            inputFormat='DD-MM-YYYY'
                            views={["year", "month", "day"]}
                            onChange={(newDate) => {
                                setFilterDate(newDate);
                            }}
                            renderInput={(params) => <TextField
                                {...params}
                                label={"Date"}
                                variant="outlined"
                                onKeyDown={(e) => e.preventDefault()}
                                sx={{ width: '15dvw', marginLeft: '1vw' }}
                                size='small'
                            />}
                        />
                    </LocalizationProvider>
                </div>
            </div>

            <Box display="flex" justifyContent="end" gap={1} mb={1}>
                <AppointmentCard onClick={() => onCardClick("")} label={"All"} count={statusCount?.total_count || 0} color={"#04B7B1"} />
                <AppointmentCard onClick={() => onCardClick("Arrived")} label={"Arrived"} count={statusCount?.arrived_count || 0} color={"#076FF8"} />
                <AppointmentCard onClick={() => onCardClick("Pending")} label={"Pending"} count={statusCount?.pending_count || 0} color={"#E09E15"} />
                <AppointmentCard onClick={() => onCardClick("Medicine Ready")} label={"Medicine Ready"} count={statusCount?.medication_ready_count || 0} color={"#B21792"} />
                <AppointmentCard onClick={() => onCardClick("Medication InProgress")} label={"InProgress"} count={statusCount?.medication_inprogress_count || 0} color={"#13B461"} />
                <AppointmentCard onClick={() => onCardClick("Completed")} label={"Completed"} count={statusCount?.completed_count || 0} color={"#0D9915"} />
            </Box>

            <Box sx={{ height: "64vh", backgroundColor: "white" }}>
                <DataGrid
                    rows={queueData}
                    columns={columns}
                    getRowId={(row) => row.id}
                    disableRowSelectionOnClick
                    headerHeight={40}
                    rowHeight={50}
                    // onCellClick={(params) => (params.row.status === "Cancelled" || params.row.status === "No Show") ? null : routeToDemograhics(params)}
                    components={{
                        NoRowsOverlay: () => (
                            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                No Records
                            </Box>
                        )
                    }}
                />
            </Box>

            {isLoading && <Loader />}
            {errorMsg ?
                <ToastMsg
                    severity={'error'}
                    msg={errorMsgText}
                    msgPop={msgClose.bind(this)}
                />
                : null}
            {successMsg ?
                <ToastMsg
                    severity={'success'}
                    msg={successMsgText}
                    msgPop={msgClose.bind(this)}
                />
                : null}
        </Box>
    );
};

export default WalkInAppointments;
